import { DrawerDialogComponent } from '@theme/drawer-dialog';
import { CartAddEvent } from '@theme/events';

/** sessionStorage key for tracking when the cart drawer was last closed. */
const LAST_OPEN_KEY = 'vandox:cart-drawer:last-open-at';

/** Fallback fresh-window in ms if the CSS var is missing or unparseable.
 *  Must stay in sync with --cart-drawer-motion-fresh-window in
 *  snippets/theme-styles-variables.liquid. */
const FRESH_WINDOW_FALLBACK_MS = 120_000;

/** Fallback max time to hold the entrance while visible media prepares.
 *  Must stay in sync with --cart-drawer-motion-prepare-timeout. */
const PREPARE_TIMEOUT_FALLBACK_MS = 350;

/** Tracks which CSS motion tokens we've already warned about this session. */
const warnedMissingTokens = new Set();

/** Number of visible cart images to promote during drawer entrance preparation. */
const HIGH_PRIORITY_IMAGE_LIMIT = 4;

/** @type {Set<string>} */
const VALID_COOLDOWN_STAGGER_MODES = new Set(['none', 'dampened']);

/**
 * @param {number} delay
 * @returns {Promise<void>}
 */
function waitForTimeout(delay) {
  return new Promise((resolve) => setTimeout(resolve, delay));
}

/**
 * @param {HTMLImageElement} image
 * @returns {Promise<void>}
 */
function waitForImage(image) {
  if (image.complete) {
    return Promise.resolve();
  }

  if (image.decode) {
    return image.decode().catch(() => {});
  }

  return new Promise((resolve) => {
    image.addEventListener('load', () => resolve(), { once: true });
    image.addEventListener('error', () => resolve(), { once: true });
  });
}

/**
 * Parse a CSS time token into milliseconds. Accepts `s`, `ms`, or a bare
 * seconds value to match the existing drawer token convention.
 * @param {string} raw
 * @param {number} fallback
 * @returns {number}
 */
function parseCssTimeValue(raw, fallback) {
  const value = raw.trim();
  if (!value) return fallback;

  const parsed = parseFloat(value);
  if (!Number.isFinite(parsed)) return fallback;

  return value.endsWith('ms') ? parsed : parsed * 1000;
}

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DrawerDialogComponent}
 */
class CartDrawerComponent extends DrawerDialogComponent {
  /** @type {number} */
  #openVersion = 0;

  /** @type {ResizeObserver | null} */
  #lookHintObserver = null;

  /** @type {(() => void) | null} */
  #lookHintCleanup = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#prewarmCartMedia();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = (event) => {
    if (event.detail.data?.didError) return;
    if (event.detail.data?.suppressCartDrawer) return;

    if (this.hasAttribute('auto-open')) {
      this.open();
    }
  };

  open() {
    if (this.refs.dialog.open) {
      // Drawer already open (e.g. auto-open fired after add-to-cart while visible).
      // Morph will update the contents in place — skip the stagger entrance entirely
      // so the blur/slide animation does not re-play on every cart state change.
      return;
    }

    const openVersion = ++this.#openVersion;
    this.#applyStaggerMode();
    this.refs.dialog.dataset.staggerState = 'preparing';
    this.showDialog();
    this.#prepareStaggerEntrance(openVersion);

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    return this.closeDialog();
  }

  closeDialog = async () => {
    if (!this.refs.dialog.open) return;

    this.#openVersion += 1;
    await this.closeDialogInternal();
    this.afterDrawerClose();
    this.#writeCloseTimestamp();
  };

  afterDrawerClose() {
    delete this.refs.dialog.dataset.staggerState;
    this.#lookHintObserver?.disconnect();
    this.#lookHintObserver = null;
    this.#lookHintCleanup?.();
    this.#lookHintCleanup = null;
    this.querySelector('.cart-drawer__look-hint')?.classList.remove('is-visible');
  }

  afterDrawerOpen() {
    if (this.dataset.itemStaggerMode !== 'block') {
      this.#tagVisibleRows();
    }
    this.#setupWillChangeCleanup();
    this.#setupLookHint();
  }

  /**
   * Remove `will-change` from each stagger item after its animation ends to
   * avoid unnecessary stacking-context promotion for the lifetime of the drawer.
   * Mirrors the same cleanup in header-drawer.js.
   */
  #setupWillChangeCleanup() {
    /**
     * @param {AnimationEvent} event
     */
    function clearWillChange(event) {
      const target = event.target;
      if (target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', clearWillChange);
      }
    }
    this.querySelectorAll('.drawer-stagger-item').forEach((el) => {
      el.addEventListener('animationend', clearWillChange);
    });
  }

  /** Read the CSS token --cart-drawer-motion-fresh-window (in seconds) → ms. */
  #getFreshWindowMs() {
    return this.#readMotionToken('--cart-drawer-motion-fresh-window', FRESH_WINDOW_FALLBACK_MS);
  }

  /** Read the CSS token --cart-drawer-motion-prepare-timeout → ms. */
  #getPrepareTimeoutMs() {
    return this.#readMotionToken('--cart-drawer-motion-prepare-timeout', PREPARE_TIMEOUT_FALLBACK_MS);
  }

  /**
   * Read a CSS motion token and warn once per session if it's missing or
   * unparseable. Loud-in-dev fallbacks prevent silent regressions if a token
   * is renamed or removed from theme-styles-variables.liquid.
   * @param {string} tokenName
   * @param {number} fallback
   */
  #readMotionToken(tokenName, fallback) {
    const raw = getComputedStyle(this).getPropertyValue(tokenName).trim();
    if (!raw && !warnedMissingTokens.has(tokenName)) {
      warnedMissingTokens.add(tokenName);
      console.warn(
        `[cart-drawer] CSS token ${tokenName} is missing; falling back to ${fallback}ms. ` +
        `Define it in snippets/theme-styles-variables.liquid.`
      );
    }
    return parseCssTimeValue(raw, fallback);
  }

  /**
   * Determine fresh vs dampened mode and stamp data-stagger-mode on the dialog
   * before showDialog() so the CSS override is in place for the first rendered frame.
   */
  #applyStaggerMode() {
    let mode = 'fresh';
    try {
      const stored = sessionStorage.getItem(LAST_OPEN_KEY);
      if (stored !== null) {
        const elapsed = Date.now() - Number(stored);
        if (elapsed < this.#getFreshWindowMs()) {
          mode = this.#getCooldownStaggerMode();
        }
      }
    } catch {
      // Private-mode storage errors — default to fresh
    }
    this.refs.dialog.dataset.staggerMode = mode;
  }

  /**
   * Read the repeat-open behavior from the cart drawer schema setting.
   * @returns {'none' | 'dampened'}
   */
  #getCooldownStaggerMode() {
    const mode = this.dataset.cooldownStaggerMode;

    if (mode && VALID_COOLDOWN_STAGGER_MODES.has(mode)) {
      return /** @type {'none' | 'dampened'} */ (mode);
    }

    return 'none';
  }

  /**
   * Write a close timestamp to sessionStorage so the next open can determine
   * whether to use the full or dampened stagger animation.
   */
  #writeCloseTimestamp() {
    try {
      sessionStorage.setItem(LAST_OPEN_KEY, String(Date.now()));
    } catch {
      // Private-mode quota errors — ignore
    }
  }

  /**
   * Start non-blocking media work before first open. This improves the common
   * header-click path without changing cart rendering or opening behavior.
   */
  #prewarmCartMedia() {
    this.#promoteVisibleCartImages();
  }

  /**
   * Hold the visible entrance until cart-row images and recommendation content
   * are ready, capped by a short timeout so slow networks still open promptly.
   * @param {number} openVersion
   */
  async #prepareStaggerEntrance(openVersion) {
    const preparation = Promise.all([
      this.#prepareVisibleCartImages(),
      this.#loadRecommendations(),
    ]);

    try {
      await Promise.race([
        preparation,
        waitForTimeout(this.#getPrepareTimeoutMs()),
      ]);
    } catch {
      // Release the entrance even if media preparation fails unexpectedly.
    }

    if (!this.#isCurrentOpen(openVersion)) return;

    this.refs.dialog.dataset.staggerState = 'ready';
  }

  /**
   * @param {number} openVersion
   * @returns {boolean}
   */
  #isCurrentOpen(openVersion) {
    return this.#openVersion === openVersion && this.refs.dialog.open;
  }

  /**
   * Promote and decode visible cart row images so row stagger delays do not
   * reveal late image paints.
   */
  async #prepareVisibleCartImages() {
    const images = this.#promoteVisibleCartImages();

    await Promise.all(images.map(waitForImage));
  }

  /**
   * @returns {HTMLImageElement[]}
   */
  #promoteVisibleCartImages() {
    const images = this.#getVisibleCartRows()
      .flatMap((row) => Array.from(row.querySelectorAll('img')))
      .filter((image) => image instanceof HTMLImageElement);

    images.forEach((image, index) => {
      image.loading = 'eager';
      image.decoding = 'async';

      if (index < HIGH_PRIORITY_IMAGE_LIMIT && 'fetchPriority' in image) {
        image.fetchPriority = 'high';
      }
    });

    return images;
  }

  /**
   * For row-mode stagger: add `drawer-stagger-item` only to rows currently
   * visible within the cart items scroll container.
   *
   * Called synchronously in afterDrawerOpen() (after showModal but before
   * the browser paints) so the CSS animation selector sees the final state.
   */
  #tagVisibleRows() {
    const rows = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('tr.cart-items__table-row')));
    if (!rows.length) return;

    // Strip stale class from all rows first (cleanup from prior opens)
    rows.forEach((row) => row.classList.remove('drawer-stagger-item'));

    this.#getVisibleCartRows(rows).forEach((row) => row.classList.add('drawer-stagger-item'));
  }

  /**
   * @param {HTMLElement[]} [rows]
   * @returns {HTMLElement[]}
   */
  #getVisibleCartRows(rows) {
    const rowList = rows ?? /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('tr.cart-items__table-row'))
    );
    if (!rowList.length) return [];

    const container = /** @type {HTMLElement | null} */ (
      this.querySelector('.cart-drawer__items')
    );

    if (!container) {
      return rowList.slice(0, 8);
    }

    const containerRect = container.getBoundingClientRect();

    if (containerRect.height === 0) {
      return rowList.slice(0, 8);
    }

    return rowList.filter((row) => {
      const rect = row.getBoundingClientRect();
      return rect.bottom > containerRect.top && rect.top < containerRect.bottom;
    });
  }

  async #loadRecommendations() {
    const recommendations = this.querySelector('#cart-drawer-recommendations');

    if (!(recommendations instanceof HTMLElement)) return;

    await customElements.whenDefined('product-recommendations');

    await recommendations.loadRecommendations?.();
  }

  async #setupLookHint() {
    const scrollContainer = this.querySelector('scroll-hint.cart-drawer__items');
    const recommendations = this.querySelector('.cart-drawer__recommendations');
    const hint = this.querySelector('.cart-drawer__look-hint');

    if (
      !(scrollContainer instanceof HTMLElement) ||
      !(recommendations instanceof HTMLElement) ||
      !(hint instanceof HTMLElement)
    ) return;

    hint.onclick = () => {
      recommendations.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    // Guard: hint stays hidden until stagger entrance completes.
    let ready = false;

    // iOS Safari: getBoundingClientRect() on elements inside an overflow-scroll
    // container returns stale layout values during composited scroll — positions
    // reflect the pre-gesture layout and don't update until the finger lifts.
    //
    // Fix: use offsetTop, which gives the element's position in scroll-content
    // coordinates (invariant to scrollTop). This works because .cart-drawer__items
    // has position:relative, making it the offsetParent for its descendants.
    // The scroll handler only reads scrollTop, which IS always current on iOS Safari.
    let recsTopInContent = 0;
    let recsBottomInContent = 0;

    const measureRecsPosition = () => {
      // offsetTop is relative to the nearest positioned ancestor (scroll-hint,
      // which has position:relative). It is invariant to scrollTop — exactly
      // what we need for scroll-position-based intersection checking.
      recsTopInContent = recommendations.offsetTop;
      recsBottomInContent = recsTopInContent + recommendations.offsetHeight;
    };

    const checkVisibility = () => {
      const scrollTop = scrollContainer.scrollTop;
      const clientHeight = scrollContainer.clientHeight;
      const isIntersecting =
        scrollTop + clientHeight > recsTopInContent && scrollTop < recsBottomInContent;
      hint.classList.toggle('is-visible', !isIntersecting);
      hint.tabIndex = isIntersecting ? -1 : 0;
    };

    const onScroll = () => {
      if (!ready) return;
      // Re-measure on every scroll — mirrors the behaviour that makes zoom work on
      // iOS Safari. The initial offsetTop reading may be stale before first paint
      // settles; re-reading is cheap (no getBoundingClientRect, no reflow when
      // layout is stable) and ensures we always use the current position.
      measureRecsPosition();
      checkVisibility();
    };

    const onLayout = () => {
      if (!ready) return;
      measureRecsPosition();
      checkVisibility();
    };

    scrollContainer.addEventListener('scroll', onScroll, { passive: true });
    this.#lookHintCleanup = () => scrollContainer.removeEventListener('scroll', onScroll);

    this.#lookHintObserver = new ResizeObserver(onLayout);
    this.#lookHintObserver.observe(scrollContainer);
    this.#lookHintObserver.observe(recommendations);

    // Wait for the stagger entrance animation to finish before revealing the hint.
    await this.#waitForStaggerComplete();
    // Guard: drawer may have been closed while we were waiting.
    if (!this.#lookHintObserver) return;

    ready = true;
    measureRecsPosition();
    checkVisibility();
  }

  /**
   * Resolves after the cart drawer stagger entrance animation finishes so the
   * look-hint does not appear while items are still animating in.
   * @returns {Promise<void>}
   */
  async #waitForStaggerComplete() {
    const dialog = this.refs.dialog;
    if (dialog.dataset.staggerMode === 'none') return;

    // Step 1: wait for stagger-state → 'ready' (animations start).
    // afterDrawerOpen runs while state is still 'preparing', so we watch for
    // the attribute flip that #prepareStaggerEntrance sets.
    if (dialog.dataset.staggerState !== 'ready') {
      await new Promise((resolve) => {
        const mo = new MutationObserver(() => {
          if (dialog.dataset.staggerState === 'ready') {
            mo.disconnect();
            resolve(undefined);
          }
        });
        mo.observe(dialog, { attributes: true, attributeFilter: ['data-stagger-state'] });
        // Safety: covers the rare case where the attribute never becomes 'ready'
        setTimeout(() => { mo.disconnect(); resolve(undefined); }, 600);
      });
    }

    // Step 2: wait for the last stagger item (highest --drawer-stagger-index) to
    // finish its animation. The summary wrapper always has the highest index.
    let lastItem = /** @type {HTMLElement | null} */ (null);
    let maxIdx = -Infinity;
    this.querySelectorAll('.drawer-stagger-item').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const n = parseInt(el.style.getPropertyValue('--drawer-stagger-index'), 10);
      if (Number.isFinite(n) && n > maxIdx) { maxIdx = n; lastItem = el; }
    });

    if (!lastItem) return;

    await new Promise((resolve) => {
      const tid = setTimeout(resolve, 1500); // safety fallback
      lastItem.addEventListener('animationend', () => { clearTimeout(tid); resolve(undefined); }, { once: true });
    });
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
